import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Download, Upload, Save, Plus, Trash2, X } from 'lucide-react';
import {
  deleteTemplate,
  findTemplateByName,
  loadTemplates,
  saveTemplate
} from '../utils/templateStorage';
import {
  APP_SCHEMA_VERSION,
  buildBlankTemplateData,
  buildBlankTemplateExport
} from '../utils/templateUtils';
import { migrateDeckTo400 } from '../utils/boppps/migrateDeckTo400';
import { repairDeck } from '../utils/boppps/repairDeck';
import { validateDeck } from '../utils/boppps/validateDeck';
import { exportBlankTemplateWithCompanionPack as exportBlankTemplateWithCompanionPackUtil } from '../utils/exportBlankTemplateWithCompanionPack';
import { HIDDEN_FILE_INPUT_CLASS, JSON_ACCEPT, openFileInput } from '../utils/filePicker';

const formatDate = (value) => {
  if (!value) return 'Unknown';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleString();
};

const sanitizeFilename = (value) => {
  const base = (value || '').toString().trim() || 'BOPPPS_Template';
  return base.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '');
};

const downloadJson = (payload, filename) => {
  const dataStr = JSON.stringify(payload, null, 2);
  const dataBlob = new Blob([dataStr], { type: 'application/json' });
  const url = URL.createObjectURL(dataBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const TemplateManager = ({
  isOpen,
  onClose,
  getCurrentLessonData,
  getDefaultLessonData,
  onApplyTemplate,
  onDownloadPresentationJson
}) => {
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [draftTemplate, setDraftTemplate] = useState(null);
  const [draftSource, setDraftSource] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [importErrors, setImportErrors] = useState([]);
  const [importStatus, setImportStatus] = useState('');
  const fileInputRef = useRef(null);

  const refreshTemplates = useCallback(() => {
    setTemplates(loadTemplates());
  }, []);

  const runDeckPipeline = (data) => {
    // Pipeline: validate → migrate (if needed) → repair → validate
    const initialValidation = validateDeck(data);
    let working = data;
    if (!working?.version || working.version !== '4.0.0') {
      const migration = migrateDeckTo400(working);
      if (!migration.ok) {
        return { ok: false, errors: migration.errors };
      }
      working = migration.deck;
    }

    const { deck: repaired } = repairDeck(working);
    const finalValidation = validateDeck(repaired);

    if (!finalValidation.ok) {
      return { ok: false, errors: finalValidation.errors };
    }

    return { ok: true, deck: repaired, initialValid: initialValidation.ok };
  };

  useEffect(() => {
    if (isOpen) {
      refreshTemplates();
    }
  }, [isOpen, refreshTemplates]);

  const sortedTemplates = useMemo(() => templates, [templates]);

  const handleCreateDraft = (source) => {
    const baseData = source === 'current' ? getCurrentLessonData() : getDefaultLessonData();
    const blankTemplate = buildBlankTemplateData(baseData);
    setDraftTemplate(blankTemplate);
    setDraftSource(source);
    setStatusMessage(`Draft created from ${source === 'current' ? 'current structure' : 'empty default'}.`);
  };

  const handleSaveTemplate = () => {
    const trimmedName = templateName.trim();
    if (!trimmedName) {
      setStatusMessage('Template name is required.');
      return;
    }
    if (!draftTemplate) {
      setStatusMessage('Create a template draft first.');
      return;
    }

    const existing = findTemplateByName(templates, trimmedName);
    if (existing && existing.id !== draftTemplate?.id) {
      const confirmed = window.confirm(`A template named "${trimmedName}" already exists. Overwrite it?`);
      if (!confirmed) return;
    }

    const saved = saveTemplate({
      id: existing?.id,
      name: trimmedName,
      description: templateDescription,
      templateJson: draftTemplate
    });

    refreshTemplates();
    setStatusMessage(`Template "${saved.name}" saved.`);
  };

  const handleLoadTemplate = (template) => {
    const confirmed = window.confirm(`Load template "${template.name}"? This will replace your current lesson.`);
    if (!confirmed) return;
    onApplyTemplate(template.templateJson);
    setStatusMessage(`Template "${template.name}" loaded.`);
    onClose();
  };

  const handleDeleteTemplate = (template) => {
    const confirmed = window.confirm(`Delete template "${template.name}"? This cannot be undone.`);
    if (!confirmed) return;
    const next = deleteTemplate(template.id);
    setTemplates(next);
  };

  const exportBlankTemplateWithCompanionPack = () => {
    exportBlankTemplateWithCompanionPackUtil({
      getLessonData: getCurrentLessonData,
      templateName,
      templateDescription
    });
    setStatusMessage('Blank template and companion pack exported for LLM use.');
  };

  const handleDownloadBlankTemplate = (template) => {
    const blankTemplate = buildBlankTemplateData(template.templateJson);
    const payload = buildBlankTemplateExport({
      templateJson: blankTemplate,
      templateName: template.name,
      description: template.description,
      schemaVersion: APP_SCHEMA_VERSION
    });
    const filenameBase = sanitizeFilename(
      payload.exportFileName || payload.slideDeckName || template.name || 'BOPPPS_Template'
    );
    downloadJson(payload, `${filenameBase}_BlankTemplate_LLM.json`);
    setStatusMessage(`Blank template "${template.name}" downloaded.`);
  };

  const handleImportCompletedJson = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (loadEvent) => {
      try {
        const parsed = JSON.parse(loadEvent.target.result);
        const pipelineResult = runDeckPipeline(parsed);
        if (!pipelineResult.ok) {
          setImportErrors(pipelineResult.errors || []);
          setImportStatus('Import failed: JSON schema validation errors found.');
          return;
        }
        setImportErrors([]);
        setImportStatus(
          pipelineResult.initialValid
            ? '✅ JSON validated successfully. Loaded into editor.'
            : '🛠️ JSON repaired and validated successfully. Loaded into editor.'
        );
        onApplyTemplate(pipelineResult.deck);
      } catch (error) {
        setImportErrors([{ path: 'root', message: 'Invalid JSON file.' }]);
        setImportStatus('Import failed: invalid JSON file.');
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Template Manager</h2>
            <p className="text-sm text-gray-500 mt-1">Create, save, and reuse blank template structures.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-200 text-gray-600"
            aria-label="Close template manager"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[70vh] space-y-6">
          <section className="grid gap-4 md:grid-cols-2">
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-800">Template Details</h3>
              <div>
                <label className="text-xs font-semibold text-gray-600">Template Name</label>
                <input
                  type="text"
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="e.g., BOPPPS Base Template"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600">Description / Notes</label>
                <textarea
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  rows={3}
                  value={templateDescription}
                  onChange={(e) => setTemplateDescription(e.target.value)}
                  placeholder="Optional notes about how to use this template."
                />
              </div>
              {statusMessage && (
                <p className="text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                  {statusMessage}
                </p>
              )}
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-800">Template Actions</h3>
              <div className="grid gap-2">
                <button
                  type="button"
                  onClick={() => handleCreateDraft('current')}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-blue-600 text-white rounded-lg shadow-sm hover:bg-blue-700"
                >
                  <Plus size={16} />
                  New Template from Current Structure
                </button>
                <button
                  type="button"
                  onClick={() => handleCreateDraft('empty')}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg shadow-sm hover:bg-indigo-700"
                >
                  <Plus size={16} />
                  New Template from Empty Default
                </button>
                <button
                  type="button"
                  onClick={handleSaveTemplate}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-emerald-600 text-white rounded-lg shadow-sm hover:bg-emerald-700"
                >
                  <Save size={16} />
                  Save Template
                </button>
                {draftSource && (
                  <p className="text-xs text-gray-600">Draft source: {draftSource === 'current' ? 'Current structure' : 'Empty default'}.</p>
                )}
              </div>
            </div>
          </section>

          <section className="space-y-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-800">Template Library</h3>
              <p className="text-xs text-gray-500 mt-1">Download any saved template as an empty structure or load it into the editor.</p>
            </div>
            {sortedTemplates.length === 0 ? (
              <p className="text-sm text-gray-500">No templates saved yet.</p>
            ) : (
              <div className="grid gap-3">
                {sortedTemplates.map((template) => (
                  <div key={template.id} className="border border-gray-200 rounded-xl p-4 bg-white shadow-sm">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{template.name}</p>
                        {template.description && (
                          <p className="text-xs text-gray-500 mt-1">{template.description}</p>
                        )}
                        <p className="text-xs text-gray-400 mt-2">Updated: {formatDate(template.updatedAt)}</p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => handleLoadTemplate(template)}
                          className="px-3 py-1.5 text-xs font-semibold text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-50"
                        >
                          Load
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDownloadBlankTemplate(template)}
                          className="px-3 py-1.5 text-xs font-semibold text-purple-700 border border-purple-200 rounded-lg hover:bg-purple-50"
                        >
                          Download Blank
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteTemplate(template)}
                          className="px-3 py-1.5 text-xs font-semibold text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-800">LLM Workflow</h3>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={exportBlankTemplateWithCompanionPack}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-purple-600 text-white rounded-lg shadow-sm hover:bg-purple-700"
                >
                  <Download size={16} />
                  Export Blank Template + Companion Pack
                </button>
                <p className="text-xs text-gray-500">
                  This export includes the blank template plus a companion pack built from your Basic BOPPPS Planner page in LessonPlanner One. If the planner is empty, the companion pack will export with empty fields. Upload both files (or the planner file) to an AI assistant to expand the outline into a completed lecture for re-import into the LessonPlanner One.
                </p>
              </div>
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => openFileInput(fileInputRef.current)}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-slate-700 text-white rounded-lg shadow-sm hover:bg-slate-800"
                >
                  <Upload size={16} />
                  Import Completed JSON
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={JSON_ACCEPT}
                  className={HIDDEN_FILE_INPUT_CLASS}
                  onChange={handleImportCompletedJson}
                />
                <button
                  type="button"
                  onClick={onDownloadPresentationJson}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-emerald-600 text-white rounded-lg shadow-sm hover:bg-emerald-700"
                >
                  <Download size={16} />
                  Download Presentation JSON
                </button>
              </div>
            </div>

            {importStatus && (
              <p className="text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                {importStatus}
              </p>
            )}
            {importErrors.length > 0 && (
              <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                <p className="font-semibold mb-1">Validation errors:</p>
                <ul className="list-disc list-inside space-y-1">
                  {importErrors.map((error, index) => (
                    <li key={`${error.path}-${index}`}>
                      <span className="font-semibold">{error.path}:</span> {error.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default TemplateManager;
